import { Flex } from '@repo/ui/Flex';
import { Box } from '@repo/ui/Box';
import { Icon } from '@repo/ui/Icon';
import { rawString, Text } from '../Language/Language';
import s from './InstructionItem.module.css';
import type { IconNames } from '@repo/ui/Icon/utils';
import type {
  InstructionContext,
  InstructionItemProps,
  InstructionsProps,
} from './types';
import { Widget } from './Widgets';
import { getBaseKeys, getAvailableInstructionCount } from './helpers';
import { usePackagingOption } from './PackagingOptionContext';

const contextToIcon: Record<InstructionContext, IconNames> = {
  label: 'print',
  items: 'box',
  location: 'map-pin',
  printerless: 'qr-code',
  info: 'info-circle',
};

export const Instructions = (props: InstructionsProps) => {
  const { deliveryMethod, deliveryType, status } = props;

  // Derive instruction count from available language keys
  const numInstructions = getAvailableInstructionCount({
    status,
    type: deliveryType,
    packaging: deliveryMethod?.packagingOption,
  });

  return [...Array(numInstructions)].map((_, i) => (
    <InstructionItem
      count={i + 1}
      type={deliveryType}
      key={`instruction-${i}`}
      deliveryMethod={deliveryMethod}
      {...props}
    />
  ));
};

const InstructionItem = ({
  count,
  type,
  status,
  label,
  qrCode,
  pollingStatus,
  orderItems,
  replacementTokens,
  location,
  deliveryMethod,
}: InstructionItemProps) => {
  const { packagingOption } = usePackagingOption();
  const baseKeys = getBaseKeys({ status, type, packaging: packagingOption });
  const titleKeys = baseKeys
    .map((key) => [
      `${key}.title.${count}.${replacementTokens?.itemCount === '1' ? 'singular' : 'plural'}`,
      `${key}.title.${count}`,
    ])
    .flat();
  const descriptionKeys = baseKeys.map((key) => `${key}.description.${count}`);
  const contexts = baseKeys.map((key) => `${key}.context.${count}`) ?? [];
  const contextKey =
    rawString({
      langKey: contexts.find((langKey) => rawString({ langKey })),
    }) ?? '[[info]]';

  const context = contextKey
    .replace(/[[\]]/g, '')
    .toLowerCase() as InstructionContext;

  const icons = baseKeys.map((key) => `${key}.icon.${count}`) ?? [];
  const iconOverride = rawString({
    langKey: icons.find((langKey) => rawString({ langKey })),
  }) as IconNames | undefined;

  const icon = iconOverride ?? contextToIcon[context];

  return (
    <Flex className={s.instructionWrapper} gap="g-16">
      <Icon name={icon ?? 'card-refund'} size="lg" className={s.icon} />
      <Flex className={s.instruction} direction="column" gap="g-4">
        <h4 className={s.instructionTitle}>
          <Text langKey={titleKeys} replacementTokens={replacementTokens} />
        </h4>
        <Box className={s.instructionDescription}>
          <Text
            langKey={descriptionKeys}
            replacementTokens={replacementTokens}
          />
        </Box>
      </Flex>
      <Widget
        {...{
          label,
          qrCode,
          deliveryMethod,
          status,
          pollingStatus,
          orderItems,
          location,
          context,
        }}
      />
    </Flex>
  );
};
